import React, { ReactNode, useState, useEffect } from "react";
import { PButtonStyle as S } from "./PButton.style";
import { Button, ButtonProps } from "primereact/button";
import { useClassNames } from "hooks/useClassNames";
import { MatIcon } from "components/MatIcon/MatIcon";
import { getSegmentService } from "services/segment.service";

export type PButtonProps = Omit<ButtonProps, "label" | "severity" | "size"> & {
  label?: string | ReactNode;
  severity?: "primary" | "secondary" | "success" | "info" | "warning" | "danger" | "help" | "contrast";
  active?: boolean;
};

export const PButton = React.forwardRef<Button, PButtonProps>((props, ref) => {
  const {
    rounded,
    raised,
    outlined,
    text,
    severity,
    active,
    label,
    disabled,
    loading,
    icon,
    iconPos,
    className,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onMouseDown,
    ...cleanProps
  } = props;
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const handleGlobalMouseUp = () => setIsActive(false);
    document.addEventListener("mouseup", handleGlobalMouseUp);
    return () => document.removeEventListener("mouseup", handleGlobalMouseUp);
  }, [isActive]);

  const classNames = useClassNames(
    {
      ["p-button-icon-" + iconPos]: !!iconPos,
      "p-button-round": !!rounded,
      "p-button-raised": !!raised,
      "p-button-filled": !outlined && !text,
      "p-button-outlined": !!outlined,
      "p-button-text": !!text,
      "p-button-primary": !severity || severity === "primary",
      "p-button-active": !!active || isActive,
      "p-button-hovered": isHovered && !disabled,
      "p-button-disabled": !!disabled || !!loading,
      "p-button-loading": !!loading,
    },
    className,
  );
  return (
    <S.PButton
      type="button"
      {...cleanProps}
      label={label as never}
      severity={severity !== "primary" ? severity : undefined}
      className={classNames}
      onMouseEnter={(e) => {
        setIsHovered(true);
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        onMouseLeave?.(e);
      }}
      onMouseDown={(e) => {
        if (!disabled) {
          setIsActive(true);
        }
        onMouseDown?.(e);
      }}
      icon={loading ? <MatIcon>progress_activity</MatIcon> : icon}
      onClick={(e) => {
        const buttonId = typeof label === "string" && label ? label : cleanProps.id;
        if (buttonId) {
          getSegmentService().trackEvent("ButtonClicked", {
            buttonId,
          });
        }
        if (disabled || loading) {
          e.preventDefault();
          e.stopPropagation();
        } else {
          onClick?.(e);
        }
      }}
      ref={ref}
    />
  );
});
